<template>
  <div
    class="min-h-screen flex bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    :class="isBottomNav ? 'h-screen min-h-0 flex-col overflow-hidden' : ''">
    <!-- Mobile sidebar overlay -->
    <Transition name="fade">
      <div
        v-if="ui.mobileSidebarOpen"
        class="fixed inset-0 z-40 bg-black/40 md:hidden"
        @click="ui.closeMobileSidebar()" />
    </Transition>

    <!-- Header nav band: GONE in portrait (card t_f6d03638). The section
         navigation is the bottom bar further down; the top band that remains is
         `TopNavigation` inside the main column (identity + title + status on
         top, actions below — card t_6a634e11, untouched). -->

    <!-- Sidebar: the docked left column in landscape/desktop, the off-canvas
         drawer on phones. In portrait the section navigation is NOT here. -->
    <aside
      v-if="placement !== 'bottom' || hasDrawer"
      :class="[
        'glass-surface h-screen pt-safe pb-safe bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col',
        // Desktop: sticky, width-animated
        'md:sticky md:top-0 md:transition-[width] md:duration-200 md:ease-in-out',
        ui.sidebarExpanded ? 'md:w-64' : 'md:w-14',
        // Mobile: fixed off-canvas drawer
        'fixed inset-y-0 left-0 z-50 w-72 transition-transform duration-200 ease-in-out md:translate-x-0',
        ui.mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
      ]">
      <div
        :class="[
          'glass-header h-14 flex items-center px-2 border-b border-gray-200 dark:border-gray-700',
          ui.sidebarExpanded || ui.mobileSidebarOpen ? 'justify-between' : 'justify-center',
        ]">
        <button
          v-if="ui.sidebarExpanded || ui.mobileSidebarOpen"
          type="button"
          class="flex items-center gap-2 min-w-0 text-left"
          @click="handleAppIconClick">
          <div
            class="w-9 h-9 flex-shrink-0 rounded-full bg-gray-300 dark:bg-gray-600 bg-cover bg-center"
            :style="`background-image: url('${system.imageUrl || brand.images.avatar}')`" />
          <span class="font-semibold truncate">{{ system.name }}</span>
        </button>
        <!-- Menu edit toggle (admin only, expanded menu only) -->
        <button
          v-if="authStore.isAdmin && ui.sidebarExpanded"
          class="hidden md:ml-auto md:flex items-center justify-center w-9 h-9 rounded-md hover:bg-black/5 dark:hover:bg-white/10 flex-shrink-0"
          :title="
            ui.sidebarEditMode ? tt('Done editing menu', 'Končaj urejanje menija') : tt('Edit menu', 'Uredi meni')
          "
          @click="ui.setSidebarEditMode(!ui.sidebarEditMode)">
          <span class="material-symbols-outlined" :class="ui.sidebarEditMode ? 'text-blue-500' : 'text-gray-500'">{{
            ui.sidebarEditMode ? 'check' : 'edit'
          }}</span>
        </button>
        <!-- Desktop collapse toggle -->
        <button
          class="hidden md:flex items-center justify-center w-9 h-9 rounded-md hover:bg-black/5 dark:hover:bg-white/10 flex-shrink-0"
          @click="handleDesktopSidebarButtonClick"
          :title="
            ui.sidebarExpanded
              ? tt('Collapse', 'Strni')
              : shouldNavigateToServers
                ? tt('Servers', 'Strežniki')
                : tt('Expand', 'Razširi')
          ">
          <div
            v-if="!ui.sidebarExpanded"
            class="w-10 h-10 flex-shrink-0 rounded-full bg-gray-300 dark:bg-gray-600 bg-cover bg-center"
            :style="`background-image: url('${system.imageUrl || brand.images.avatar}')`" />
          <span v-else class="material-symbols-outlined text-gray-500">menu_open</span>
        </button>
        <!-- Mobile close button -->
        <button
          class="md:hidden flex items-center justify-center w-9 h-9 rounded-md hover:bg-black/5 dark:hover:bg-white/10 text-gray-500"
          @click="ui.closeMobileSidebar()">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
      <div
        :class="['flex-1 min-h-0 overflow-y-auto', !(ui.sidebarExpanded || ui.mobileSidebarOpen) && 'hide-scrollbar']">
        <NavigationMenu @navigate="ui.closeMobileSidebar()" />
      </div>
    </aside>

    <!-- Main column -->
    <div class="flex-1 min-w-0 flex flex-col overflow-hidden" :class="isBottomNav ? 'min-h-0' : 'h-screen'">
      <TopNavigation />
      <main class="flex-1 overflow-y-auto pb-safe">
        <slot />
      </main>

      <BrowserPlayerBar />
    </div>

    <!-- Portrait: the section navigation is a bar at the BOTTOM edge (card
         t_f6d03638). It is the shell column's last child, so `main`'s scroll
         area ends exactly at its top edge — no page row ever hides behind it —
         and `pb-safe` keeps it above the home indicator. -->
    <div
      v-if="isBottomNav"
      data-bottom-nav
      class="glass-surface h-bar-safe-bottom w-full flex-shrink-0 border-t border-gray-200 bg-white pb-safe dark:border-gray-700 dark:bg-gray-800">
      <div class="flex h-full items-stretch">
        <NavigationMenu horizontal stacked class="min-w-0 flex-1" />
      </div>
    </div>

    <!-- User Drawer: in-flow when pinned, fixed overlay when floating -->
    <UserDrawer />
    <StatusDrawer />
    <SiteDrawer />
    <PlaylistDrawer />

    <!-- Agent FAB (conditional on user preference) … and, on a demo hub that
         replaces it, the DEMO FAB in the same corner instead (card D2, plan
         §4.8). `ui.showDemoFab` is the user's own toggle; whether a FAB is
         there at all is the hub's `demo_mode.showFab`, read by the component
         itself — so a normal hub mounts nothing and costs one GET. -->
    <AgentFab v-if="ui.showAgentFab && !demoReplaceAgentFab" />
    <DemoFab v-if="ui.showDemoFab" :stacked="ui.showAgentFab && !demoReplaceAgentFab" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import NavigationMenu from '../components/NavigationMenu.vue';
import TopNavigation from '../components/TopNavigation.vue';
import UserDrawer from '../components/UserDrawer.vue';
import StatusDrawer from '../components/StatusDrawer.vue';
import SiteDrawer from '../components/SiteDrawer.vue';
import PlaylistDrawer from '../components/media/PlaylistDrawer.vue';
import AgentFab from '../components/AgentFab.vue';
import DemoFab from '../components/demo/DemoFab.vue';
import BrowserPlayerBar from '../components/media/BrowserPlayerBar.vue';
import { useUIStore } from '../stores/ui';
import { useSystemStore } from '../stores/system';
import { useSidebarConfigStore } from '../stores/sidebarConfig';
import { useDashboardStore } from '../stores/dashboard';
import { useAuthStore } from '../stores/auth';
import { useWebRTCStore } from '../stores/webrtc';
import { isElectron, isCloud } from '../config/runtime';
import { brand } from '../config/brand';
import { tt } from '../utils/consoleText';
import { useBottomNavMode } from '../composables/useWideViewport';
import { useDemoPlayerView } from '../composables/useDemoPlayerView';

const ui = useUIStore();
const system = useSystemStore();
const authStore = useAuthStore();
const webrtcStore = useWebRTCStore();
const route = useRoute();
const router = useRouter();
const sidebarConfigStore = useSidebarConfigStore();
const dashboardStore = useDashboardStore();
/**
 * Does the hub's demo mode take the agent FAB's corner? (card D2, plan §4.8:
 * `replaceAgentFab` defaults ON — two FABs in one corner is the accident, and
 * keeping both is the operator's explicit choice.) The same composable the FAB
 * uses, so the layout and the button can never disagree about the mode; a hub
 * that is not a demo answers `false` and nothing here changes.
 */
const { replaceAgentFab: demoReplaceAgentFab } = useDemoPlayerView();
const isCloudLandingPage = computed(
  () =>
    route.path === '/cloud' ||
    route.path === '/home' ||
    route.path === '/servers' ||
    (!!route.meta.cloudView && !route.params.hubId),
);
const shouldNavigateToServers = computed(
  () => isElectron() || isCloud() || typeof route.params.hubId !== 'undefined' || isCloudLandingPage.value,
);

function handleAppIconClick() {
  // The brand icon is NOT a navigation trigger in the Electron shell (leaving a
  // hub happens via logout / kiosk exits) — treat it as a sidebar toggle there.
  // Local mode toggles the sidebar too, not a home navigation that jumps to
  // /rooms/overview.
  if (!shouldNavigateToServers.value || isElectron()) {
    if (ui.mobileSidebarOpen) {
      ui.closeMobileSidebar();
      return;
    }
    ui.toggleSidebar();
    return;
  }

  // Web builds: the brand icon returns to the dashboard.
  ui.closeMobileSidebar();
  void router.push('/home');
}

function handleDesktopSidebarButtonClick() {
  if (ui.sidebarExpanded) {
    ui.toggleSidebar();
    return;
  }
  handleAppIconClick();
}

/**
 * Where the section navigation lives, by device shape — one shared decision
 * (`useWideViewport.navPlacementFor`, card t_f6d03638):
 *   - portrait → the BOTTOM bar (phones AND tablets: the owner asked for the
 *     section tabs to leave the top band);
 *   - landscape / desktop (≥768px) → the docked left column, unchanged;
 *   - a phone held sideways → the off-canvas drawer (the bar would eat the page,
 *     and `hasDrawer` keeps the drawer rendered beside it).
 */
const { placement, isBottomNav, hasDrawer } = useBottomNavMode();

onMounted(() => {
  if (isCloudLandingPage.value) {
    return;
  }

  system.load();
  sidebarConfigStore.load();
  if (dashboardStore.dashboards.length === 0) {
    dashboardStore.loadDashboards(route.params.hubId);
  }
});

// Reload location branding (name + image) once a live connection exists —
// auth may restore after mount, or a cloud WebRTC session may connect later.
watch(
  () => authStore.isAuthenticated || webrtcStore.connected,
  (active) => {
    if (active) system.load();
  },
  { immediate: true },
);

// The mobile drawer never survives a route change. The dashboard switcher no
// longer hides on scroll: it rides in the top bar (card: the switcher moved up),
// which sits outside the scrolling column, so it is always on screen.
watch(
  () => route.path,
  () => {
    ui.closeMobileSidebar();
  },
);
</script>

<style scoped>
.hide-scrollbar {
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
